
import React, { useState, useEffect } from "react";
import { Layout } from "@/components/layout/Layout";
import { TutorialCourse } from "@/components/tutorials/TutorialCourse";
import { TutorialCourseDetail } from "@/components/tutorials/TutorialCourseDetail";
import { Button } from "@/components/ui/button";
import { Check, Home, ChevronLeft, Grid2X2, ListIcon } from "lucide-react";

const COURSES = [
  {
    id: 1,
    title: "Body Language Masterclass",
    progress: 0,
    image: "/lovable-uploads/368089c9-dc8d-47c3-af5c-f00bc647eaca.png",
    instructor: "Hoss Immo Consulting GmbH",
    instructorImage: "/lovable-uploads/ce32cef5-b142-46aa-900a-767fcecbfc13.png",
    description: "Mit der Mission, den Vertrieb neu zum Leben zu erwecken, gründete sich Hoss Immo. Heute zählt Hoss Immo zu den aufstrebenden und erfolgreichsten deutschen Vertriebsagenturen.",
    lessons: [
      {
        id: 1,
        title: "Die Grundphilosophie der menschlichen Tonalität & Körpersprache",
        duration: "1:52",
        completed: true,
        videoUrl: "https://example.com/video1.mp4",
        thumbnail: "/lovable-uploads/4639941b-464f-41f0-8edb-cf12d26f72f1.png"
      },
      {
        id: 2,
        title: "Wie wirke ich kompetent?",
        duration: "1:48",
        completed: true,
        videoUrl: "https://example.com/video2.mp4",
        thumbnail: "/lovable-uploads/4639941b-464f-41f0-8edb-cf12d26f72f1.png"
      },
      {
        id: 3,
        title: "Wirkung auf andere Menschen",
        duration: "2:51",
        completed: true,
        videoUrl: "https://example.com/video3.mp4",
        thumbnail: "/lovable-uploads/4639941b-464f-41f0-8edb-cf12d26f72f1.png"
      },
      {
        id: 4,
        title: "Kompetente Wirkung durch Körpersprache",
        duration: "11:32",
        completed: true,
        videoUrl: "https://example.com/video4.mp4",
        thumbnail: "/lovable-uploads/4639941b-464f-41f0-8edb-cf12d26f72f1.png"
      },
      {
        id: 5,
        title: "Impuls erhöhen durch Veränderung",
        duration: "8:48",
        completed: true,
        videoUrl: "https://example.com/video5.mp4",
        thumbnail: "/lovable-uploads/4639941b-464f-41f0-8edb-cf12d26f72f1.png"
      },
      {
        id: 6,
        title: "Wie vermeide ich rezessive Verhaltensmuster?",
        duration: "3:32",
        completed: true,
        videoUrl: "https://example.com/video6.mp4",
        thumbnail: "/lovable-uploads/4639941b-464f-41f0-8edb-cf12d26f72f1.png"
      },
      {
        id: 7,
        title: "Wie gewinne ich das Interesse einer Person?",
        duration: "5:18",
        completed: true,
        videoUrl: "https://example.com/video7.mp4",
        thumbnail: "/lovable-uploads/4639941b-464f-41f0-8edb-cf12d26f72f1.png"
      }
    ]
  },
  {
    id: 2,
    title: "Hoss Immo Vertriebsgenie Executive",
    progress: 0,
    image: "/lovable-uploads/368089c9-dc8d-47c3-af5c-f00bc647eaca.png",
    instructor: "Hoss Immo Consulting GmbH",
    instructorImage: "/lovable-uploads/ce32cef5-b142-46aa-900a-767fcecbfc13.png",
    description: "Mit besonderer Spezialisierung auf den Bereichen der digitalen Vertriebsprozesse bieten wir individuell entwickelte Lösungsansätze zur Entwicklung optimaler Vertriebsstrategien.",
    lessons: []
  },
  {
    id: 3,
    title: "Die Prinzipien der Skalierung",
    progress: 0,
    image: "/lovable-uploads/368089c9-dc8d-47c3-af5c-f00bc647eaca.png",
    instructor: "Hoss Immo Consulting GmbH",
    instructorImage: "/lovable-uploads/ce32cef5-b142-46aa-900a-767fcecbfc13.png",
    description: "Durch die Zusammenarbeit mit über 200 Dienstleistern ist eine ganzheitliche Vertriebsberatung, die einen messbaren Neukundenzuwachs sowie nachhaltigen Wachstum versichert, garantiert.",
    lessons: []
  }
];

const Tutorials = () => {
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedCourse, setSelectedCourse] = useState<number | null>(null);
  const [activeLessonId, setActiveLessonId] = useState<number | null>(null);
  const [watchedLessons, setWatchedLessons] = useState<Record<number, boolean>>({});
  // Add coursesWithProgress to track calculated progress
  const [coursesWithProgress, setCoursesWithProgress] = useState(COURSES);

  // Get the selected course data
  const currentCourse = selectedCourse !== null
    ? coursesWithProgress.find(course => course.id === selectedCourse)
    : null;

  // Get the active lesson data
  const currentLesson = activeLessonId !== null && currentCourse
    ? currentCourse.lessons.find(lesson => lesson.id === activeLessonId)
    : null;

  // Load watched lessons from localStorage on component mount
  useEffect(() => {
    const savedWatchedLessons = localStorage.getItem("watchedLessons");
    if (savedWatchedLessons) {
      setWatchedLessons(JSON.parse(savedWatchedLessons));
    }
  }, []);

  // Calculate progress for all courses whenever watchedLessons changes
  useEffect(() => {
    const updatedCourses = COURSES.map(course => {
      // If the course has no lessons, progress remains 0
      if (course.lessons.length === 0) return { ...course, progress: 0 };
      
      // Count watched lessons for this course
      const watchedCount = course.lessons.filter(lesson => 
        watchedLessons[lesson.id] === true
      ).length;
      
      // Calculate progress percentage
      const progress = Math.round((watchedCount / course.lessons.length) * 100);
      
      // Update the completed status for each lesson
      const updatedLessons = course.lessons.map(lesson => ({
        ...lesson,
        completed: watchedLessons[lesson.id] === true
      }));
      
      return { 
        ...course, 
        progress,
        lessons: updatedLessons
      };
    });
    
    setCoursesWithProgress(updatedCourses);
  }, [watchedLessons]);

  // Mark current lesson as watched after 10 seconds of viewing
  useEffect(() => {
    if (!currentLesson) return;
    
    const timer = setTimeout(() => {
      const updatedWatchedLessons = { 
        ...watchedLessons, 
        [currentLesson.id]: true 
      };
      
      setWatchedLessons(updatedWatchedLessons);
      localStorage.setItem("watchedLessons", JSON.stringify(updatedWatchedLessons));
    }, 10000); // 10 seconds
    
    return () => clearTimeout(timer);
  }, [currentLesson, watchedLessons]);

  const handleBackToOverview = () => {
    setSelectedCourse(null);
    setActiveLessonId(null);
  };

  // Navigate to previous lesson
  const goToPreviousLesson = () => {
    if (!currentCourse || !currentLesson) return;
    
    const currentIndex = currentCourse.lessons.findIndex(lesson => lesson.id === currentLesson.id);
    if (currentIndex > 0) {
      setActiveLessonId(currentCourse.lessons[currentIndex - 1].id);
    }
  };

  // Navigate to next lesson
  const goToNextLesson = () => {
    if (!currentCourse || !currentLesson) return;
    
    const currentIndex = currentCourse.lessons.findIndex(lesson => lesson.id === currentLesson.id);
    if (currentIndex < currentCourse.lessons.length - 1) {
      setActiveLessonId(currentCourse.lessons[currentIndex + 1].id);
    }
  };

  return (
    <Layout>
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        {!selectedCourse ? (
          <>
            {/* Courses Overview Page */}
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-semibold">Meine Kurse</h1>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  className={viewMode === "grid" ? "bg-[#41c7f0]/10 text-[#41c7f0] border-[#41c7f0]/30" : ""}
                >
                  <Grid2X2 size={18} />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setViewMode("list")}
                  className={viewMode === "list" ? "bg-[#41c7f0]/10 text-[#41c7f0] border-[#41c7f0]/30" : ""}
                >
                  <ListIcon size={18} />
                </Button>
              </div>
            </div>

            <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "flex flex-col gap-4"}>
              {coursesWithProgress.map((course) => (
                <TutorialCourse
                  key={course.id}
                  course={course}
                  viewMode={viewMode}
                  onSelect={() => setSelectedCourse(course.id)}
                />
              ))}
            </div>
          </>
        ) : currentLesson ? (
          // Lesson View - Video Player Page
          <div className="w-full">
            <div className="flex items-center gap-2 mb-4 text-sm font-medium text-gray-400">
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-0 hover:bg-transparent hover:text-[#41c7f0]"
                onClick={handleBackToOverview}
              >
                <Home size={14} />
              </Button>
              <span>/</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-0 hover:bg-transparent hover:text-[#41c7f0]"
                onClick={() => setActiveLessonId(null)}
              >
                {currentCourse?.title}
              </Button>
              <span>/</span>
              <span className="text-white">{currentLesson.title}</span>
            </div>

            <div className="glass-morphism rounded-xl overflow-hidden">
              <h1 className="text-xl font-semibold p-4 border-b border-gray-800/50">
                {currentLesson.title}
              </h1>
              
              <div className="aspect-video bg-black">
                {/* In a real app, this would be an actual video player */}
                <div className="w-full h-full flex items-center justify-center">
                  <img 
                    src={currentLesson.thumbnail} 
                    alt={currentLesson.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              <div className="p-4 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={goToPreviousLesson}
                    disabled={currentCourse.lessons.findIndex(lesson => lesson.id === currentLesson.id) === 0}
                  >
                    <ChevronLeft size={16} className="mr-1" /> Vorherige Lektion
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={goToNextLesson}
                    disabled={currentCourse.lessons.findIndex(lesson => lesson.id === currentLesson.id) === currentCourse.lessons.length - 1}
                  >
                    Nächste Lektion <ChevronLeft size={16} className="ml-1 rotate-180" />
                  </Button>
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-400">
                  <span>Lektion {currentLesson.id} von {currentCourse?.lessons.length}</span>
                </div>
              </div>
            </div>
            
            <div className="mt-6 glass-morphism rounded-xl p-4">
              <h2 className="text-lg font-medium mb-4">Weitere Lektionen in diesem Kurs</h2>
              <ul className="divide-y divide-gray-800/50">
                {currentCourse?.lessons.map(lesson => (
                  <li 
                    key={lesson.id} 
                    className={`flex items-center gap-4 py-3 cursor-pointer hover:bg-gray-800/20 px-2 rounded-lg ${lesson.id === currentLesson.id ? 'bg-[#41c7f0]/10' : ''}`}
                    onClick={() => setActiveLessonId(lesson.id)}
                  >
                    <div className="relative w-16 h-10 rounded-md overflow-hidden flex-shrink-0">
                      <img src={lesson.thumbnail} alt="" className="w-full h-full object-cover" />
                      <div className={`absolute inset-0 flex items-center justify-center bg-black/20 
                        ${lesson.completed ? 'bg-[#41c7f0]/40' : ''}`}>
                        {lesson.completed && (
                          <div className="bg-[#41c7f0] rounded-full p-1">
                            <Check size={12} />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 text-left">
                      <p className={`${lesson.id === currentLesson.id ? 'text-[#41c7f0]' : ''}`}>{lesson.title}</p>
                      <p className="text-sm text-gray-400">{lesson.duration}</p>
                    </div>
                    {lesson.id === currentLesson.id && (
                      <div className="w-1.5 h-6 bg-[#41c7f0] rounded-full ml-2"></div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          // Course Detail Page
          <div className="w-full">
            <div className="flex items-center gap-2 mb-4 text-sm font-medium text-gray-400">
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-0 hover:bg-transparent hover:text-[#41c7f0]"
                onClick={handleBackToOverview}
              >
                <Home size={14} />
              </Button>
              <span>/</span>
              <span className="text-white">{currentCourse?.title}</span>
            </div>

            <TutorialCourseDetail 
              course={currentCourse} 
              onSelectLesson={(lessonId) => setActiveLessonId(lessonId)}
              onBack={handleBackToOverview}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Tutorials;
